import { useState, useEffect } from 'react';
import { HeartIcon, CartIcon } from '../components/Icons';
import { toast } from '../toast';

export default function ProductDetailPage({ product, products, onNavigate, onAddToCart, onAddToWishlist, isWished }) {
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [selectedSize, setSelectedSize] = useState(product?.sizes?.[0] || '');
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || '');

  useEffect(() => {
    if (product?.sizes?.length > 0 && (!selectedSize || !product.sizes.includes(selectedSize))) {
      setSelectedSize(product.sizes[0]);
    }
    if (product?.colors?.length > 0 && (!selectedColor || !product.colors.includes(selectedColor))) {
      setSelectedColor(product.colors[0]);
    }
  }, [product, selectedSize, selectedColor]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  // Lock body scroll when fullscreen is active
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF3F0] gap-4">
        <div className="w-16 h-16 bg-[#FEF0E6] rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-[#F47B20]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-gray-500 font-medium">Product not found</p>
        <button onClick={() => onNavigate('shop')} className="px-6 py-2 bg-[#F47B20] text-white rounded-xl font-bold text-sm">Back to Shop</button>
      </div>
    );
  }

  let gallery = (product.images && product.images.length > 0)
    ? [...product.images]
    : product.image
      ? [product.image]
      : [];

  // Sort videos to the front
  gallery.sort((a, b) => {
    const aIsVideo = a.match(/\.(mp4|webm|ogg)$/i);
    const bIsVideo = b.match(/\.(mp4|webm|ogg)$/i);
    if (aIsVideo && !bIsVideo) return -1;
    if (!aIsVideo && bIsVideo) return 1;
    return 0;
  });

  const activeImage = gallery[activeImageIdx] || null;

  const goNext = (e) => {
    if (e) e.stopPropagation();
    setActiveImageIdx(i => (i + 1) % gallery.length);
  };
  const goPrev = (e) => {
    if (e) e.stopPropagation();
    setActiveImageIdx(i => (i - 1 + gallery.length) % gallery.length);
  };

  // Lightbox gestures
  let touchStartX = 0;
  const handleTouchStart = (e) => { touchStartX = e.changedTouches[0].screenX; };
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].screenX;
    if (touchEndX < touchStartX - 50) goNext();
    if (touchEndX > touchStartX + 50) goPrev();
  };

  return (
    <div className="min-h-screen bg-white pb-24 md:pb-8 lg:bg-[#FAF3F0]">
      {/* ── Main Container (Fluid for Desktop) ── */}
      <div className="w-full mx-auto lg:px-12 xl:px-16 lg:py-8 lg:flex lg:gap-10 lg:items-start">
        
        {/* ════ LEFT SIDE: Image Gallery ════ */}
        <div className="lg:w-[55%] xl:w-3/5 lg:bg-white lg:rounded-3xl lg:overflow-hidden lg:shadow-sm lg:border lg:border-gray-100">
          <div className="relative w-full aspect-square md:aspect-auto md:h-[70vh] lg:h-[80vh] bg-gray-50 cursor-pointer overflow-hidden group" onClick={() => setIsFullscreen(true)}>
        
        {/* Main Image */}
        {activeImage ? (
          activeImage.match(/\.(mp4|webm|ogg)$/i) ? (
            <video
              src={activeImage}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <img
              src={activeImage}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50">
            <svg className="w-16 h-16 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Out of stock overlay */}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-white/30 backdrop-blur-[2px] flex items-center justify-center z-10 pointer-events-none">
            <svg className="w-24 h-24 text-red-500/90 drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        )}

        {/* Gradient Overlay for Top Icons visibility */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/30 to-transparent pointer-events-none z-20" />

        {/* Top Icons Layer */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none z-30">
          {/* Back Button */}
          <button 
            onClick={(e) => { e.stopPropagation(); onNavigate('shop'); }} 
            className="w-10 h-10 bg-white/20 backdrop-blur-md hover:bg-white rounded-full flex items-center justify-center text-white hover:text-black transition-all shadow-sm pointer-events-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>

          {/* Right Icons: Share & Wishlist */}
          <div className="flex flex-col gap-3 pointer-events-auto">
            <button
              onClick={(e) => { e.stopPropagation(); onAddToWishlist(product.id); }}
              className="w-10 h-10 bg-white/20 backdrop-blur-md hover:bg-white rounded-full flex items-center justify-center text-white hover:text-[#F47B20] transition-all shadow-sm"
            >
              <HeartIcon className="w-5 h-5" filled={isWished} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const shareUrl = `${window.location.origin}/product/${product.id}`;
                if (navigator.share) {
                  navigator.share({ title: product.name, url: shareUrl })
                    .catch(err => {
                      if (err.name !== 'AbortError') {
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Link copied to clipboard!');
                      }
                    });
                } else {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success('Link copied to clipboard!');
                }
              }}
              className="w-10 h-10 bg-white/20 backdrop-blur-md hover:bg-white rounded-full flex items-center justify-center text-white hover:text-black transition-all shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
            </button>
          </div>
        </div>

        {/* Image Counter & Expand Hint */}
        <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end pointer-events-none">
          <div className="bg-black/40 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-[10px] font-bold shadow-sm tracking-wider">
            {gallery.length > 1 ? `${activeImageIdx + 1} / ${gallery.length}` : '1 / 1'}
          </div>
          <div className="bg-black/40 backdrop-blur-sm text-white p-2 rounded-full shadow-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
          </div>
        </div>
      </div>
      </div>

      {/* ════ RIGHT SIDE: Product Info Panel ════ */}
      <div className="lg:w-[45%] xl:w-2/5 max-w-3xl mx-auto lg:mx-0 px-5 py-6 lg:p-8 lg:bg-white lg:rounded-3xl lg:shadow-sm lg:border lg:border-gray-100 lg:sticky lg:top-24">
        <div className="flex flex-col mb-4 pb-4 border-b border-gray-100">
          <h1 className="text-xl lg:text-3xl font-bold text-[#1A1A2E] leading-snug tracking-tight mb-2">{product.name}</h1>
          
          <div className="flex items-center gap-3">
            <p className="text-2xl font-black text-[#F47B20]">
              {product.priceStr || `₦${Number(product.price || 0).toLocaleString()}`}
            </p>
            {product.original_price && product.original_price > product.price ? (
              <p className="text-base font-semibold text-gray-400 line-through">
                {`₦${Number(product.original_price).toLocaleString()}`}
              </p>
            ) : null}
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest bg-gray-100 px-2 py-1 rounded-sm">
              VAT INCLUSIVE
            </span>
          </div>

          {product.stock !== undefined && (
            <div className="mt-3 flex items-center gap-2">
              <p className={`text-[11px] font-semibold ${
                product.stock === 0 ? 'text-red-500' : 'text-green-600'
              }`}>
                {product.stock === 0 ? 'Out of stock' : `${product.stock} unit${product.stock === 1 ? '' : 's'} left in stock`}
              </p>
            </div>
          )}
        </div>

        {/* Sizes */}
        {product.sizes && product.sizes.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-bold text-[#1A1A2E] uppercase tracking-wide">Select Size</h4>
              <button onClick={() => setShowSizeGuide(true)} className="text-[11px] font-bold text-gray-500 underline hover:text-[#F47B20] transition-colors">SIZE GUIDE</button>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {Array.from(new Set(product.sizes)).map((size, idx) => (
                <button
                  key={`${size}-${idx}`}
                  onClick={() => setSelectedSize(size)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold border-2 transition-all min-w-[70px] ${
                    selectedSize === size
                      ? 'bg-[#1A1A2E] text-white border-[#1A1A2E] shadow-md shadow-[#1A1A2E]/20'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#1A1A2E] hover:text-[#1A1A2E]'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Colors */}
        {product.colors && product.colors.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-bold text-[#1A1A2E] uppercase tracking-wide">Select Color</h4>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {Array.from(new Set(product.colors)).map((color, idx) => (
                <button
                  key={`${color}-${idx}`}
                  onClick={() => setSelectedColor(color)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold border-2 transition-all min-w-[70px] ${
                    selectedColor === color
                      ? 'bg-[#1A1A2E] text-white border-[#1A1A2E] shadow-md shadow-[#1A1A2E]/20'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#1A1A2E] hover:text-[#1A1A2E]'
                  }`}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2.5 mb-8">
          <button
            disabled={product.stock === 0}
            onClick={() => { onAddToCart(product, selectedSize, selectedColor); onNavigate('cart'); }}
            className={`flex-1 font-bold py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-sm flex items-center justify-center gap-1.5 transition-colors ${
              product.stock === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-[#F47B20] text-white hover:bg-[#D96A10] active:scale-95'
            }`}
          >
            <CartIcon className="w-3.5 h-3.5" /> Add to Cart
          </button>
          <button
            disabled={product.stock === 0}
            onClick={() => { onAddToCart(product, selectedSize, selectedColor); onNavigate('checkout'); }}
            className={`flex-1 font-bold py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-sm flex items-center justify-center transition-colors ${
              product.stock === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 active:scale-95'
            }`}
          >
            Buy Now
          </button>
        </div>

        {/* Description & Specs */}
        <div className="mb-8">
          <div className="text-[13px] text-gray-600 leading-relaxed font-medium space-y-3">
            {(product.description || product.desc || 'No description available for this product.')
              .split(/\n\n+/)
              .map((paragraph, pIdx) => (
                <p key={pIdx}>
                  {paragraph.split(/\n/).map((line, lIdx, arr) => (
                    <span key={lIdx}>
                      {line}
                      {lIdx < arr.length - 1 && <br />}
                    </span>
                  ))}
                </p>
              ))
            }
          </div>

          <div className="flex flex-col gap-3 mt-5">
            {product.material && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider w-20">Material</span>
                <span className="text-[13px] font-semibold text-[#1A1A2E]">{product.material}</span>
              </div>
            )}
            {product.shipping && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider w-20">Shipping</span>
                <span className="text-[13px] font-semibold text-[#1A1A2E]">{product.shipping}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ratings & Reviews */}
        <div className="mb-8 border-t border-gray-100 pt-6">
          <h3 className="text-base font-semibold text-[#1A1A2E] mb-4 flex items-center gap-2">
            Ratings & Reviews
            {product.ratings?.length > 0 && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-bold">
                {product.ratings.length}
              </span>
            )}
          </h3>
          
          {product.ratings?.length > 0 ? (
            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
              {product.ratings.map((rating, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm text-[#1A1A2E]">{rating.user?.name || 'Customer'}</span>
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className={`w-3.5 h-3.5 ${i < rating.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  {rating.review && (
                    <p className="text-[13px] text-gray-600 leading-relaxed">{rating.review}</p>
                  )}
                  <p className="text-[10px] text-gray-400 mt-2">
                    {new Date(rating.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No reviews yet. Be the first to rate this product after delivery!</p>
          )}
        </div>

        {/* Similar Products */}
        {(() => {
          const similarProducts = (products || [])
            .filter(p => p.id !== product.id && p.category_id === product.category_id)
            .slice(0, 4);

          if (similarProducts.length < 4) {
            const ids = new Set(similarProducts.map(p => p.id));
            ids.add(product.id);
            const fallbacks = (products || []).filter(p => !ids.has(p.id)).slice(0, 4 - similarProducts.length);
            similarProducts.push(...fallbacks);
          }

          if (similarProducts.length === 0) return null;

          return (
            <div className="mt-12">
              <h3 className="text-base font-semibold text-[#1A1A2E] mb-4">You May Also Like</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {similarProducts.map(sim => {
                  const simImg = Array.isArray(sim.images) 
                    ? sim.images.find(img => !img.match(/\.(mp4|webm|ogg)$/i)) 
                    : (!sim.image?.match(/\.(mp4|webm|ogg)$/i) ? sim.image : null);
                  return (
                  <div
                    key={sim.id}
                    onClick={() => { onNavigate('productdetail', { product: sim }); window.scrollTo(0, 0); }}
                    className="group cursor-pointer bg-white p-2 rounded-2xl shadow-[0_2px_10px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)] transition-shadow duration-300"
                  >
                    <div className="aspect-[4/5] bg-gray-50 overflow-hidden rounded-xl mb-2">
                      {simImg ? (
                        <img src={simImg} alt={sim.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-200">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        </div>
                      )}
                    </div>
                    <div className="px-1 pb-1">
                      <p className="font-bold text-[#1A1A2E] text-xs uppercase tracking-wider truncate mb-0.5">{sim.name}</p>
                      <p className="font-semibold text-gray-500 text-xs">{sim.priceStr || `₦${(sim.price || 0).toLocaleString()}`}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
      </div>

      {/* ── Fullscreen Lightbox ── */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {/* Header */}
          <div className="absolute top-0 inset-x-0 p-4 flex justify-between items-center z-10 bg-gradient-to-b from-black/50 to-transparent">
            <div className="text-white text-xs font-bold tracking-widest bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-sm">
              {activeImageIdx + 1} / {gallery.length}
            </div>
            <button onClick={() => setIsFullscreen(false)} className="p-2 bg-black/40 text-white rounded-full hover:bg-white/20 transition-colors backdrop-blur-sm">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Image */}
          <div className="flex-1 flex items-center justify-center relative overflow-hidden" onClick={(e) => {
             // Close if clicked on background
             if (e.target === e.currentTarget) setIsFullscreen(false);
          }}>
            {activeImage && (
              activeImage.match(/\.(mp4|webm|ogg)$/i) ? (
                <video
                  src={activeImage}
                  autoPlay
                  controls
                  playsInline
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <img
                  src={activeImage}
                  alt={`${product.name} view ${activeImageIdx + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
              )
            )}
            
            {/* Desktop Navigation Arrows */}
            {gallery.length > 1 && (
              <>
                <button onClick={goPrev} className="hidden md:flex absolute left-6 p-4 rounded-full bg-white/10 text-white hover:bg-white hover:text-black transition-colors backdrop-blur-md">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button onClick={goNext} className="hidden md:flex absolute right-6 p-4 rounded-full bg-white/10 text-white hover:bg-white hover:text-black transition-colors backdrop-blur-md">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {gallery.length > 1 && (
            <div className="p-4 bg-gradient-to-t from-black/80 to-transparent flex gap-3 overflow-x-auto justify-center pb-8 scrollbar-hide">
              {gallery.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIdx(idx)}
                  className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                    activeImageIdx === idx ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                >
                  {img.match(/\.(mp4|webm|ogg)$/i) ? (
                    <video src={img} muted playsInline className="w-full h-full object-cover" />
                  ) : (
                    <img src={img} alt="Thumbnail" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* ── SIZE GUIDE MODAL ── */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="font-bold text-[#1A1A2E] text-lg">Size Guide</h3>
              <button onClick={() => setShowSizeGuide(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-200">
                    <th className="px-4 py-3 font-semibold rounded-tl-lg">Size</th>
                    <th className="px-4 py-3 font-semibold">UK</th>
                    <th className="px-4 py-3 font-semibold">US</th>
                    <th className="px-4 py-3 font-semibold rounded-tr-lg">EU</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-bold text-[#1A1A2E]">XS</td>
                    <td className="px-4 py-3 text-gray-600">6</td>
                    <td className="px-4 py-3 text-gray-600">2</td>
                    <td className="px-4 py-3 text-gray-600">34</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-bold text-[#1A1A2E]">S</td>
                    <td className="px-4 py-3 text-gray-600">8</td>
                    <td className="px-4 py-3 text-gray-600">4</td>
                    <td className="px-4 py-3 text-gray-600">36</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-bold text-[#1A1A2E]">M</td>
                    <td className="px-4 py-3 text-gray-600">10</td>
                    <td className="px-4 py-3 text-gray-600">6</td>
                    <td className="px-4 py-3 text-gray-600">38</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-bold text-[#1A1A2E]">L</td>
                    <td className="px-4 py-3 text-gray-600">12</td>
                    <td className="px-4 py-3 text-gray-600">8</td>
                    <td className="px-4 py-3 text-gray-600">40</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-bold text-[#1A1A2E]">XL</td>
                    <td className="px-4 py-3 text-gray-600">14</td>
                    <td className="px-4 py-3 text-gray-600">10</td>
                    <td className="px-4 py-3 text-gray-600">42</td>
                  </tr>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-bold text-[#1A1A2E]">XXL</td>
                    <td className="px-4 py-3 text-gray-600">16</td>
                    <td className="px-4 py-3 text-gray-600">12</td>
                    <td className="px-4 py-3 text-gray-600">44</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-gray-400 mt-4 text-center">Measurements are standard approximations.</p>
            </div>
            <div className="p-4 bg-gray-50 text-right">
              <button onClick={() => setShowSizeGuide(false)} className="px-5 py-2 bg-[#1A1A2E] text-white text-sm font-bold rounded-xl hover:bg-[#2A2A4E] transition-colors">Got it</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
